import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Box, Button, Paper, Typography, TextField, Grid, IconButton, Chip, CircularProgress } from '@material-ui/core'
import { Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon, Cancel as CancelIcon } from '@material-ui/icons'
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import { ja } from 'date-fns/locale'
import { PrimaryConfirmModal } from '../../../molecules/ModalContainer/PrimaryDialogTemplate/PrimaryConfirmDialog'
import { ModalsContext } from '../../../../contexts'
import { formatOtacMaintenanceDateTime } from '../../../../libs/date'
import { generateTolOperationsItemId } from '../../../../libs/functions/tolOperationsItemId'
import { TolOtacMaintenance, ValidationError } from '../../../../types/tolOperations'
import {
  OTAC_STATUS_LABELS,
  OtacMaintenanceStatus,
  getPublishStatusFromDates,
  parseOtacDate,
} from '../shared/periodStatus'

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  scheduleCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    border: '4px solid transparent',
    borderRadius: theme.spacing(1),
  },
  scheduleCardEdited: {
    borderLeftColor: theme.palette.primary.main,
  },
  scheduleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statusChips: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  formControl: {
    width: '100%',
  },
  deleteButton: {
    color: theme.palette.error.main,
  },
  addButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
  },
  actionButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(8),
  },
}))

/** フォーム編集用のメンテナンススケジュール。日時は日時ピッカーで扱うため Date で保持する */
export interface FormOtacSchedule {
  id: string
  maintenanceStartAt: Date | null
  maintenanceEndAt: Date | null
  description: string
}

export interface FieldErrors {
  maintenanceStartAt?: string
  maintenanceEndAt?: string
  description?: string
}

interface OtacMaintenanceFormPageProps {
  existingSchedules: TolOtacMaintenance[]
  isLoading: boolean
  isSaving: boolean
  serverErrors: ValidationError[]
  onClearServerErrors: () => void
  onSave: (items: TolOtacMaintenance[]) => Promise<void>
  onCancel: () => void
}

const DESCRIPTION_MAX_LENGTH = 500

const createEmptySchedule = (usedIds: Iterable<string>): FormOtacSchedule => ({
  id: generateTolOperationsItemId(usedIds),
  maintenanceStartAt: null,
  maintenanceEndAt: null,
  description: '',
})

const toFormSchedule = (schedule: TolOtacMaintenance): FormOtacSchedule => ({
  id: schedule.id,
  maintenanceStartAt: parseOtacDate(schedule.maintenanceStartAt),
  maintenanceEndAt: parseOtacDate(schedule.maintenanceEndAt),
  description: schedule.description || '',
})

const isValidDate = (date: Date | null): date is Date => date !== null && !isNaN(date.getTime())

const ENDED_SCHEDULE_GUIDE = 'メンテナンスが終了したスケジュールは保存できません。削除してください'

// 開始・終了日時から判定したメンテナンス状態。日時が未入力・不正な場合、または対象がない場合（新規行の編集前）は null
const getScheduleStatus = (
  schedule: FormOtacSchedule | undefined,
  now: Date = new Date(),
): OtacMaintenanceStatus | null =>
  schedule && isValidDate(schedule.maintenanceStartAt) && isValidDate(schedule.maintenanceEndAt)
    ? getPublishStatusFromDates(schedule.maintenanceStartAt, schedule.maintenanceEndAt, now)
    : null

/**
 * バリデーション入力チェック。エラーがなければ空オブジェクトを返す
 * - 実施済み: 編集不可。削除を促すエラーを返す
 * - 実行中: 編集・削除ともに不可。チェック対象外とする
 */
export const validateOtacSchedule = (
  schedule: FormOtacSchedule,
  beforeEdit: FormOtacSchedule | undefined,
  now: Date,
): FieldErrors => {
  const beforeEditStatus = getScheduleStatus(beforeEdit, now)

  if (beforeEditStatus === 'ended') {
    return { maintenanceEndAt: ENDED_SCHEDULE_GUIDE }
  }

  if (beforeEditStatus === 'active') {
    return {}
  }

  const itemErrors: FieldErrors = {}

  if (schedule.description.length > DESCRIPTION_MAX_LENGTH) {
    itemErrors.description = '説明は500文字以内で入力してください'
  }

  if (!schedule.maintenanceStartAt) {
    itemErrors.maintenanceStartAt = 'メンテナンス開始日時は必須です'
  } else if (!isValidDate(schedule.maintenanceStartAt)) {
    itemErrors.maintenanceStartAt = '日付の形式が正しくありません'
  }

  if (!schedule.maintenanceEndAt) {
    itemErrors.maintenanceEndAt = 'メンテナンス終了日時は必須です'
  } else if (!isValidDate(schedule.maintenanceEndAt)) {
    itemErrors.maintenanceEndAt = '日付の形式が正しくありません'
  } else if (schedule.maintenanceEndAt <= now) {
    itemErrors.maintenanceEndAt = 'メンテナンス終了日時には現在時刻より後の日時を入力してください'
  } else if (isValidDate(schedule.maintenanceStartAt) && schedule.maintenanceStartAt >= schedule.maintenanceEndAt) {
    itemErrors.maintenanceEndAt = 'メンテナンス終了日時は開始日時より後である必要があります'
  }

  return itemErrors
}

const toApiSchedule = (schedule: FormOtacSchedule): TolOtacMaintenance => {
  const description = schedule.description.trim()
  return {
    id: schedule.id,
    maintenanceStartAt: formatOtacMaintenanceDateTime(schedule.maintenanceStartAt as Date),
    maintenanceEndAt: formatOtacMaintenanceDateTime(schedule.maintenanceEndAt as Date),
    ...(description ? { description } : {}),
  }
}

const DATE_INPUT_GUIDE = 'キーボード入力する場合は半角数字で入力してください'

// エラー時も案内文を消さず2行目に表示し続ける
const buildDateHelperText = (error: string | undefined): React.ReactNode =>
  error ? (
    <>
      {error}
      <Typography component="span" display="block" variant="caption" color="textSecondary">
        {DATE_INPUT_GUIDE}
      </Typography>
    </>
  ) : (
    DATE_INPUT_GUIDE
  )

// 一括削除対象のID一覧
const buildBulkDeletePreview = (items: { id: string }[]): string => items.map(item => `・${item.id}`).join('\n')

export const OtacMaintenanceFormPage: FC<OtacMaintenanceFormPageProps> = ({
  existingSchedules,
  isLoading,
  isSaving,
  serverErrors,
  onClearServerErrors,
  onSave,
  onCancel,
}) => {
  const classes = useStyles()
  const { appendModal, closeAllModals } = useContext(ModalsContext)
  const [schedules, setSchedules] = useState<FormOtacSchedule[]>([])
  const [fieldErrors, setFieldErrors] = useState<FieldErrors[]>([])
  const [editedScheduleIds, setEditedScheduleIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (existingSchedules.length === 0) {
      setSchedules([createEmptySchedule([])])
    } else {
      setSchedules(existingSchedules.map(toFormSchedule))
    }
    setFieldErrors([])
    setEditedScheduleIds(new Set())
  }, [existingSchedules])

  // 編集前（読み込み時）の値
  const beforeEditSchedules = useMemo(
    () => new Map(existingSchedules.map(schedule => [schedule.id, toFormSchedule(schedule)])),
    [existingSchedules],
  )

  // 実施済みの行のID。判定は編集前（読み込み時）の値で行う。
  const endedScheduleIds = useMemo(() => {
    const now = new Date()
    return new Set(
      schedules
        .filter(schedule => getScheduleStatus(beforeEditSchedules.get(schedule.id), now) === 'ended')
        .map(schedule => schedule.id),
    )
  }, [schedules, beforeEditSchedules])
  const endedCount = endedScheduleIds.size

  const getServerFieldError = (index: number, field: string): string | undefined => {
    return serverErrors.find(e => e.field === `[${index}].${field}` || e.field === `${index}.${field}`)?.message
  }

  const validate = (): boolean => {
    const now = new Date()
    const errors = schedules.map(schedule =>
      validateOtacSchedule(schedule, beforeEditSchedules.get(schedule.id), now),
    )
    setFieldErrors(errors)
    return errors.every(item => Object.keys(item).length === 0)
  }

  const handleAddSchedule = () => {
    const usedIds = schedules.map(s => s.id)
    setSchedules([...schedules, createEmptySchedule(usedIds)])
  }

  const handleDelete = (index: number) => {
    if (getScheduleStatus(beforeEditSchedules.get(schedules[index].id)) === 'active') return
    appendModal(
      <PrimaryConfirmModal
        header="メンテナンススケジュールの削除"
        content="このメンテナンススケジュールを削除しますか？"
        label="削除"
        skipButton={true}
        onSubmit={() => {
          closeAllModals()
          onClearServerErrors()
          const deletedId = schedules[index].id
          setSchedules(schedules.filter((_, i) => i !== index))
          setFieldErrors(fieldErrors.filter((_, i) => i !== index))
          setEditedScheduleIds(prev => {
            if (!prev.has(deletedId)) return prev
            const next = new Set(prev)
            next.delete(deletedId)
            return next
          })
        }}
      />,
    )
  }

  const handleBulkDeleteEnded = () => {
    const ended = schedules.filter(schedule => endedScheduleIds.has(schedule.id))
    if (ended.length === 0) {
      return
    }

    appendModal(
      <PrimaryConfirmModal
        header="メンテナンス終了分の一括削除"
        content={
          <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
            {`メンテナンス終了済みのスケジュール ${ended.length} 件を削除します。\n\n${buildBulkDeletePreview(
              ended,
            )}\n\nよろしいですか？`}
          </Typography>
        }
        label="削除"
        skipButton={true}
        onSubmit={() => {
          closeAllModals()
          onClearServerErrors()
          const endedIds = new Set(ended.map(schedule => schedule.id))
          setSchedules(schedules.filter(schedule => !endedIds.has(schedule.id)))
          setFieldErrors([])
          setEditedScheduleIds(prev => new Set([...prev].filter(id => !endedIds.has(id))))
        }}
      />,
    )
  }

  const handleChange = (index: number, field: keyof FormOtacSchedule, value: string | Date | null) => {
    const updated = [...schedules]
    updated[index] = { ...updated[index], [field]: value }
    setSchedules(updated)
    setEditedScheduleIds(prev => {
      if (prev.has(updated[index].id)) return prev
      const next = new Set(prev)
      next.add(updated[index].id)
      return next
    })
  }

  const handleSave = async () => {
    if (!validate()) return
    onSave(schedules.map(toApiSchedule))
  }

  if (isLoading) {
    return (
      <Box className={classes.loadingState}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box className={classes.header}>
        <Typography variant="h5" component="h2">
          OTACメンテナンススケジュール設定
        </Typography>
      </Box>

      {schedules.map((schedule, index) => {
        const maintenanceStatus = getScheduleStatus(schedule)
        const beforeEditStatus = getScheduleStatus(beforeEditSchedules.get(schedule.id))
        // 実行中: 編集・削除不可 / 実施済み: 編集不可（削除のみ可）
        const isStarted = beforeEditStatus === 'active' || beforeEditStatus === 'ended'
        const startedGuide =
          beforeEditStatus === 'active' ? 'メンテナンス実行中のスケジュールは編集・削除できません' : ENDED_SCHEDULE_GUIDE
        return (
          <Paper
            key={schedule.id}
            className={`${classes.scheduleCard} ${
              editedScheduleIds.has(schedule.id) ? classes.scheduleCardEdited : ''
            }`}
          >
            <Box className={classes.scheduleHeader}>
              <Box className={classes.statusChips}>
                {maintenanceStatus && <Chip label={OTAC_STATUS_LABELS[maintenanceStatus]} size="small" />}
                {editedScheduleIds.has(schedule.id) && (
                  <Chip label="編集済み（未保存）" size="small" color="primary" />
                )}
              </Box>
              <Box>
                <IconButton
                  className={classes.deleteButton}
                  size="small"
                  onClick={() => handleDelete(index)}
                  disabled={beforeEditStatus === 'active'}
                  aria-label="削除"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="ID"
                  value={schedule.id}
                  className={classes.formControl}
                  InputProps={{ readOnly: true }}
                  error={!!getServerFieldError(index, 'id')}
                  helperText={getServerFieldError(index, 'id')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ja}>
                  <KeyboardDateTimePicker
                    label="メンテナンス開始日時"
                    value={schedule.maintenanceStartAt}
                    onChange={date => handleChange(index, 'maintenanceStartAt', date)}
                    format="yyyy/MM/dd HH:mm"
                    className={classes.formControl}
                    error={
                      !!fieldErrors[index]?.maintenanceStartAt || !!getServerFieldError(index, 'maintenanceStartAt')
                    }
                    helperText={
                      isStarted
                        ? startedGuide
                        : buildDateHelperText(
                            fieldErrors[index]?.maintenanceStartAt || getServerFieldError(index, 'maintenanceStartAt'),
                          )
                    }
                    disabled={isStarted}
                    disablePast
                    required
                    ampm={false}
                  />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ja}>
                  <KeyboardDateTimePicker
                    label="メンテナンス終了日時"
                    value={schedule.maintenanceEndAt}
                    onChange={date => handleChange(index, 'maintenanceEndAt', date)}
                    format="yyyy/MM/dd HH:mm"
                    className={classes.formControl}
                    error={!!fieldErrors[index]?.maintenanceEndAt || !!getServerFieldError(index, 'maintenanceEndAt')}
                    helperText={
                      isStarted
                        ? startedGuide
                        : buildDateHelperText(
                            fieldErrors[index]?.maintenanceEndAt || getServerFieldError(index, 'maintenanceEndAt'),
                          )
                    }
                    disabled={isStarted}
                    disablePast
                    minDate={isValidDate(schedule.maintenanceStartAt) ? schedule.maintenanceStartAt : undefined}
                    ampm={false}
                    required
                  />
                </MuiPickersUtilsProvider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="説明"
                  value={schedule.description}
                  className={classes.formControl}
                  multiline
                  maxRows={4}
                  error={!!fieldErrors[index]?.description || !!getServerFieldError(index, 'description')}
                  helperText={
                    isStarted
                      ? startedGuide
                      : fieldErrors[index]?.description ||
                        getServerFieldError(index, 'description') ||
                        'メンテナンスの説明を入力してください（任意）'
                  }
                  disabled={isStarted}
                  onChange={e => handleChange(index, 'description', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        )
      })}

      <Box textAlign="center" className={classes.addButton}>
        <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={handleAddSchedule}>
          メンテナンススケジュールを追加
        </Button>
      </Box>

      <Box className={classes.buttonGroup}>
        <Button variant="outlined" color="secondary" onClick={handleBulkDeleteEnded} disabled={endedCount === 0}>
          メンテナンス終了分を一括削除（{endedCount}件）
        </Button>
        <Box className={classes.actionButtons}>
          <Button variant="outlined" startIcon={<CancelIcon />} onClick={onCancel} disabled={isSaving}>
            キャンセル
          </Button>
          <Button variant="contained" color="primary" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving}>
            保存
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
